/**
 * Admin Broadcasts page
 *
 * Compose a raw-HTML announcement, preview it, send a test to yourself, then
 * blast the single user list (every approved, non-unsubscribed user). The body
 * is wrapped server-side in the 4K Labs branding + an unsubscribe footer.
 *
 * Flow: edit subject/body → Save draft → Send test → type SEND → Send to all.
 * Editing after a save clears the draft so test/send always reflect what's saved.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { RefreshCw, Send, FlaskConical, Save } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

export const Route = createFileRoute('/admin/broadcasts')({
  component: AdminBroadcasts,
});

type BroadcastStatus = 'DRAFT' | 'SENDING' | 'SENT' | 'FAILED';

interface Broadcast {
  id: string;
  subject: string;
  bodyHtml: string;
  status: BroadcastStatus;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  sentAt: string | null;
}

const STATUS_VARIANT: Record<BroadcastStatus, 'default' | 'secondary' | 'destructive'> = {
  DRAFT: 'secondary',
  SENDING: 'default',
  SENT: 'default',
  FAILED: 'destructive',
};

function AdminBroadcasts() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [audienceSize, setAudienceSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Composer
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function loadBroadcasts() {
    setLoading(true);
    try {
      const r = await api.get('/admin/broadcasts');
      setBroadcasts(r.data.broadcasts);
      setAudienceSize(r.data.audienceSize);
    } catch (e) {
      console.error('Failed to load broadcasts', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadBroadcasts(); }, []);

  // Any edit invalidates the saved draft — force a re-save before test/send.
  function editSubject(v: string) { setSubject(v); setDraftId(null); setConfirmText(''); }
  function editBody(v: string) { setBodyHtml(v); setDraftId(null); setConfirmText(''); }

  function fail(err: any, fallback: string) {
    setError(err?.response?.data?.error || err?.message || fallback);
  }

  async function handleSaveDraft(): Promise<string | null> {
    setError(null); setNotice(null);
    if (!subject.trim()) { setError('Enter a subject'); return null; }
    if (!bodyHtml.trim()) { setError('Enter an HTML body'); return null; }
    setBusy(true);
    try {
      const r = await api.post('/admin/broadcasts', { subject: subject.trim(), bodyHtml });
      const id = r.data.broadcast.id as string;
      setDraftId(id);
      setNotice('Draft saved.');
      await loadBroadcasts();
      return id;
    } catch (err: any) {
      fail(err, 'Failed to save draft');
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function handleSendTest() {
    setError(null); setNotice(null);
    const id = draftId || (await handleSaveDraft());
    if (!id) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/broadcasts/${id}/test`);
      setNotice(`Test sent to ${r.data.sentTo}.`);
    } catch (err: any) {
      fail(err, 'Failed to send test');
    } finally {
      setBusy(false);
    }
  }

  async function handleSend() {
    setError(null); setNotice(null);
    if (!draftId) { setError('Save the draft first.'); return; }
    if (confirmText.trim() !== 'SEND') { setError('Type SEND to confirm.'); return; }
    if (!confirm(`Send "${subject}" to ${audienceSize ?? 'all'} recipients? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/broadcasts/${draftId}/send`);
      setNotice(`Sending to ${r.data.totalRecipients} recipients.`);
      setSubject(''); setBodyHtml(''); setDraftId(null); setConfirmText('');
      await loadBroadcasts();
    } catch (err: any) {
      fail(err, 'Failed to send broadcast');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Broadcasts</h1>
          <p className="text-muted-foreground">
            Send a product update to every approved, non-unsubscribed user
            {audienceSize !== null && <> — <span className="font-semibold text-foreground">{audienceSize}</span> recipients right now</>}.
          </p>
        </div>
        <Button variant="outline" onClick={loadBroadcasts} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}
      {notice && (
        <div className="rounded border border-green-500/50 bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">{notice}</div>
      )}

      {/* Composer */}
      <Card>
        <CardHeader><CardTitle>Compose</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => editSubject(e.target.value)} placeholder="What's new in 4K Labs" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label>HTML body</Label>
              <textarea
                className="mt-1 block w-full h-72 rounded border bg-background px-3 py-2 font-mono text-xs leading-relaxed"
                value={bodyHtml}
                onChange={(e) => editBody(e.target.value)}
                placeholder={'<h1>Big news</h1>\n<p>We shipped something great…</p>\n<p><a href="https://app.4klabs.ai">Try it</a></p>'}
                spellCheck={false}
              />
            </div>
            <div>
              <Label>Body preview <span className="text-muted-foreground font-normal">(wrapped in 4K Labs branding + unsubscribe footer when sent)</span></Label>
              <iframe
                title="preview"
                className="mt-1 block w-full h-72 rounded border bg-white"
                sandbox=""
                srcDoc={bodyHtml || '<p style="color:#9ca3af;font-family:sans-serif;padding:16px;">Preview appears here…</p>'}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={handleSaveDraft} disabled={busy}>
              <Save className="h-4 w-4 mr-1" /> Save draft
            </Button>
            <Button variant="outline" onClick={handleSendTest} disabled={busy}>
              <FlaskConical className="h-4 w-4 mr-1" /> Send test to me
            </Button>

            <div className="flex-1" />

            <div>
              <Label className="text-xs">Type SEND to confirm</Label>
              <Input
                className="w-40"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SEND"
                disabled={!draftId}
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={busy || !draftId || confirmText.trim() !== 'SEND'}
            >
              <Send className="h-4 w-4 mr-1" /> Send to {audienceSize ?? 'all'}
            </Button>
          </div>
          {!draftId && (subject || bodyHtml) && (
            <p className="text-xs text-muted-foreground">Save the draft to enable test + send.</p>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader><CardTitle>Past broadcasts</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center min-h-[160px]">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Sent / Total</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {broadcasts.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-medium max-w-[280px] truncate">{b.subject}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[b.status]}>{b.status.toLowerCase()}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.status === 'DRAFT' ? '—' : `${b.sentCount} / ${b.totalRecipients}`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.failedCount > 0 ? <span className="text-destructive">{b.failedCount}</span> : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(b.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {broadcasts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No broadcasts yet. Compose one above.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
